/**
 * UNPINNING A TILE — the state of the two-step control, as a pure function.
 *
 * ══ WHY THIS EXISTS ════════════════════════════════════════════════════════
 *
 * `DELETE /v1/admin/agent/pin` shipped with the library, and the artifacts hook
 * has exposed `unpinAnswer()` since then. NO SCREEN EVER CALLED IT. A pinned
 * tile could not be removed, and — because "the token lives on the pin row, so
 * deleting the pin kills every link to it" (admin-agent-library.ts) — a share
 * link could not be revoked by the admin who created it. The server's only
 * revocation had no button.
 *
 * ══ WHY TWO STEPS, AND WHY NOT `window.confirm` ════════════════════════════
 *
 * Unpinning is not undoable from this screen: the tile is gone, and if it was
 * shared, so is every link to it. So the first click only ARMS the control and
 * says what will happen; the second click does it. A browser dialog was
 * rejected — it cannot say the share-link sentence conditionally in the page's
 * own voice, it blocks the whole tab, and it is invisible to a screen reader's
 * live region.
 *
 * Pure on purpose: the repository tests admin screens as logic (root jest, no
 * DOM), so the rule lives where a test can reach it.
 */

export type UnpinPhase = 'idle' | 'confirming' | 'removing' | 'failed';

export interface UnpinState {
  readonly phase: UnpinPhase;
  /** Operator-readable, set only in `failed`. */
  readonly error: string | null;
}

export type UnpinEvent =
  | { readonly type: 'ask' }
  | { readonly type: 'keep' }
  | { readonly type: 'confirm' }
  | { readonly type: 'failed'; readonly message: string };

export const UNPIN_IDLE: UnpinState = Object.freeze({ phase: 'idle', error: null });

/**
 * The transitions. Anything not listed leaves the state unchanged — in
 * particular a second `confirm` while `removing` is a no-op, so a double click
 * can never send two DELETEs.
 */
export function unpinReducer(state: UnpinState, event: UnpinEvent): UnpinState {
  switch (event.type) {
    case 'ask':
      return state.phase === 'idle' || state.phase === 'failed'
        ? { phase: 'confirming', error: null }
        : state;
    case 'keep':
      return state.phase === 'confirming' ? UNPIN_IDLE : state;
    case 'confirm':
      return state.phase === 'confirming' ? { phase: 'removing', error: null } : state;
    case 'failed':
      return state.phase === 'removing' ? { phase: 'failed', error: event.message } : state;
    default:
      return state;
  }
}

/** True exactly when the DELETE may be sent: the transition INTO `removing`. */
export function shouldSendUnpin(before: UnpinState, after: UnpinState): boolean {
  return before.phase === 'confirming' && after.phase === 'removing';
}

/**
 * What the armed control says. The share-link sentence is CONDITIONAL on the
 * server's own `shared` flag: claiming a revocation that does not happen would
 * be as untrue as hiding one that does.
 */
export function unpinConfirmationText(pin: { title: string; shared?: boolean }): string {
  const base = `Remove “${pin.title}” from your dashboard?`;
  return pin.shared === true
    ? `${base} Its share link stops working for everyone who has it.`
    : base;
}
